/**
 * tm/components/ConsultRecordings.tsx — 동행상담 상세(로움 어드민)의 「녹음 · 대화 글」 구역 (2026-10-28 지도사 「클로바노트처럼」)
 *  녹음 올리기(진행률 · 300MB) → 서버가 Groq 받아쓰기 + 우리 서버 화자 구분(없으면 CLOVA)으로 화자별 대화 글 · 정리 중이면 30초마다 다시 봄 · 화자 이름 · 지우기 · 다시 정리
 *  FP 포털 · WM 포털에도 같은 부품(RecordingView)으로 보인다
 *  상담 결과 입력 창 · 상담 상세 둘 다 이 부품 · 팀장님 · WM 께는 동행상담 결과 보내기로 함께 간다(따로 문자 없음 · 2026-10-28 지도사)
 *  녹음 파일은 30일 보관 · 그 뒤엔 대화 글만(2026-10-28 · 서버 consultRec.purgeOld)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { C, T } from './ui';
import { apiToken } from '../api';
import RecordingView, { SpeakerCountChips } from './RecordingView';
import type { Rec } from './RecordingView';
import { KEEP_NOTE, isCallAudio } from '../callRec';
import { useFileDrop } from '../useFileDrop';


const API = '/api/rec';
const MAX_MB = 300;

export default function ConsultRecordings({ consultId, onToast }: { consultId: string; onToast?: (m: string, t?: 'success' | 'error') => void }) {
  const [list, setList] = useState<Rec[] | null>(null);
  const [ready, setReady] = useState(true);
  const [pct, setPct] = useState<number | null>(null);
  const [spk, setSpk] = useState(0);   // 올릴 때 화자 수(0 = 자동 · 2026-10-29)
  // 고른 파일 — 바로 올리지 않고 「몇 명이 말했나요」를 이 파일 옆에서 묻는다(2026-10-29 지도사 「4명 눌렀는데 여전히」: 올리기 칸 위 화자 수를 위 녹음 것으로 알고 누름)
  const [pending, setPending] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const toast = (m: string, t: 'success' | 'error' = 'success') => { if (onToast) onToast(m, t); };

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/consultations/${consultId}`, { headers: { Authorization: 'Bearer ' + apiToken() } });
      const j = await r.json() as { success: boolean; data?: { list: Rec[]; ready: boolean } };
      if (j.success && j.data) { setList(j.data.list); setReady(j.data.ready); }
    } catch { setList(l => l || []); }
  }, [consultId]);
  useEffect(() => { void load(); }, [load]);
  // 정리 중이면 30초마다
  useEffect(() => {
    if (!list || !list.some(x => x.status === 'processing' || (x.status === 'queued' && ready) || !!x.relabeling)) return undefined;   // 화자 다시 나누는 중도
    const t = window.setTimeout(() => { void load(); }, 30000);   // 녹음 1시간 정리에 15분쯤(Groq 무료 한도 분당 20번이 대부분) — 30초마다면 충분
    return () => window.clearTimeout(t);
  }, [list, ready, load]);

  /** 고르기 · 끌어다 놓기 → 확인 칸(화자 수) */
  const pick = (f: File) => {
    if (!isCallAudio(f) && !/^video\//.test(f.type)) { toast('녹음(소리 · 영상) 파일만 올릴 수 있어요', 'error'); return; }   // 끌어다 놓기는 accept 가 걸러 주지 않음
    if (f.size > MAX_MB * 1048576) { toast(`녹음 파일은 ${MAX_MB}MB 까지예요`, 'error'); return; }
    setSpk(0); setPending(f);
  };
  const upload = (f: File) => {
    setPending(null);
    const fd = new FormData(); if (spk) fd.append('speakers', String(spk)); fd.append('file', f);
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/consultations/${consultId}`);
    x.setRequestHeader('Authorization', 'Bearer ' + apiToken());
    x.upload.onprogress = e => { if (e.lengthComputable) setPct(Math.round((e.loaded / e.total) * 100)); };
    x.onload = () => {
      setPct(null);
      let j: { success?: boolean; error?: string; data?: { ready: boolean } } = {};
      try { j = JSON.parse(x.responseText) as typeof j; } catch { /* 아래에서 */ }
      if (x.status === 200 && j.success) { toast(j.data && j.data.ready ? '올렸어요 · 대화 글로 정리하고 있어요 · 결과 보내기로 팀장님 · WM 께 함께 가요' : '올렸어요 · 녹음 정리 열쇠를 넣으면 대화 글로 정리돼요'); void load(); }
      else toast(j.error || '올리지 못했어요', 'error');
    };
    x.onerror = () => { setPct(null); toast('올리지 못했어요 · 연결을 확인해 주세요', 'error'); };
    setPct(0); x.send(fd);
  };
  const drop = useFileDrop(pick, pct !== null);   // 끌어다 놓기(2026-10-29) — 이 구역 어디에 놓아도
  const call = async (method: string, path: string, body?: unknown, okMsg?: string) => {
    try {
      const r = await fetch(`${API}${path}`, { method, headers: { Authorization: 'Bearer ' + apiToken(), 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      const j = await r.json() as { success: boolean; error?: string };
      if (!j.success) { toast(j.error || '저장하지 못했어요', 'error'); return; }
      if (okMsg) toast(okMsg);
      void load();
    } catch { toast('저장하지 못했어요', 'error'); }
  };

  return (
    <div {...drop.bind} style={{ display: 'grid', gap: 10 }}>
      {(list || []).map(rec => (
        <RecordingView key={rec.id} rec={rec} editable keyReady={ready}
          onRename={sp => void call('PUT', `/${rec.id}/speakers`, { speakers: sp }, '화자 이름을 저장했어요 · FP · WM 포털에도 같이 보여요')}
          onMerge={(from, to) => void call('PUT', `/${rec.id}/merge-speaker`, { from, to }, '화자를 합쳤어요')}
          onRetry={() => void call('POST', `/${rec.id}/retry`, undefined, '다시 정리하고 있어요')}
          onSpeakerCount={n => void call('PUT', `/${rec.id}/speaker-count`, { n }, `화자를 ${n}명 안에서 다시 나누고 있어요 · 몇 분 걸려요`)}
          onDelete={() => void call('DELETE', `/${rec.id}`, undefined, '녹음을 지웠어요')} />
      ))}
      <input ref={fileRef} type="file" accept="audio/*,video/*,.m4a,.mp3,.wav,.aac,.ogg,.flac" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) pick(f); }} />
      {pending ? (
        // 올릴 녹음 확인 — 이 파일에서 몇 명이 말했는지(모르면 자동)
        <div style={{ border: `1.5px solid ${C.accent}`, borderRadius: 10, padding: 12, display: 'grid', gap: 10, background: C.accentLt }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
            <b style={{ flex: 1, minWidth: 0, fontSize: T.sm, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pending.name}</b>
            <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{(pending.size / 1048576).toFixed(1)}MB</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.xs, fontWeight: 700, color: C.text }}>이 녹음에서 몇 명이 말했나요</span>
            <SpeakerCountChips value={spk} onPick={setSpk} auto />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={() => upload(pending)} style={{ width: 96, height: 34, borderRadius: 8, border: 'none', background: C.accent, color: '#fff', fontSize: T.sm, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>올리기</button>
            <button type="button" onClick={() => setPending(null)} style={{ width: 96, height: 34, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.text, fontSize: T.sm, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>취소</button>
          </div>
        </div>
      ) : <button type="button" disabled={pct !== null} onClick={() => fileRef.current && fileRef.current.click()}
        style={{ height: 40, borderRadius: 10, border: `1.5px dashed ${drop.over ? C.accent : C.borderDk}`, background: drop.over ? C.accentLt : '#fff', color: drop.over ? C.accent : C.text, fontSize: T.sm, fontWeight: 600, cursor: pct === null ? 'pointer' : 'progress', fontFamily: 'inherit' }}>
        {pct !== null ? `올리는 중 ${pct}%` : drop.over ? '여기에 놓으면 골라져요' : '녹음 올리기 · 끌어다 놓아도 돼요 (m4a · mp3 · wav · 영상 · 300MB까지)'}
      </button>}
      <div style={{ fontSize: T.xs, color: C.muted }}>화자별 대화 글은 지도사님 · 그 상담의 FP 포털 · WM 포털에만 보여요. 발언을 누르면 그 부분부터 재생돼요. {KEEP_NOTE}.</div>
    </div>
  );
}
